<template lang="pug">
.schedule-print
  header.schedule-print__header
    .schedule-print__event
      h1.schedule-print__event-title {{ eventTitle }}
      p.schedule-print__event-dates {{ eventDates }}
    .schedule-print__scope-label {{ scopeLabel }}
  .schedule-print__rule

  .schedule-print__filters(v-if="hasActiveFilters")
    span.schedule-print__filters-label Filtered by:
    span.schedule-print__filter-chip(v-if="dayLabel") {{ dayLabel }}
    span.schedule-print__filter-chip(
      v-for="chip in activeFilterChips"
      :key="chip") {{ chip }}

  template(v-if="groupingMode === 'time-grouped'")
    section.schedule-print__day(v-for="day in days" :key="day.label")
      .day-print-band {{ day.label }}
      template(v-for="group in day.groups")
        .schedule-print__concurrent-header(
          v-if="group.sessions.length > 1") Concurrent Sessions
        .session-print-row(
          v-for="session in group.sessions"
          :key="session.id || session.title")
          .session-print-row__time {{ formatTime(session.meta.startDate) }} – {{ formatTime(session.meta.endDate) }}
          .session-print-row__main
            .session-print-row__title {{ session.title }}
            .session-print-row__meta(v-if="session.meta.sessionRoom") {{ session.meta.sessionRoom }}
            .session-print-row__tags
              span.session-print-row__tag(v-if="session.meta.sessionType") {{ session.meta.sessionType }}
              span.session-print-row__tag(v-if="isPreregRequired(session)") Pre-reg Required
              span.session-print-row__tag.session-print-row__tag--credit(
                v-if="creditFor(session)") {{ creditFor(session) }}
              span.session-print-row__tag(
                v-for="t in (session.meta.sessionTopics || [])"
                :key="t") {{ t }}

  template(v-else)
    .session-print-row(v-for="session in sessions" :key="session.id || session.title")
      .session-print-row__time {{ formatTime(session.meta.startDate) }} – {{ formatTime(session.meta.endDate) }}
      .session-print-row__main
        .session-print-row__title {{ session.title }}
        .session-print-row__meta(v-if="session.meta.sessionRoom") {{ session.meta.sessionRoom }}
</template>

<script>
import { groupByStartTime, formatDayLabel } from './scheduleResultGrouping.js';
import { formatTime, formatDate } from './scheduleTimeUtils.js';

export default {
  name: 'SchedulePrintView',

  props: {
    sessions: { type: Array, default: () => [] },
    groupingMode: { type: String, default: 'time-grouped' }, // 'time-grouped' | 'flat'
    scope: { type: String, default: 'all' }, // 'all' | 'current'
  },

  computed: {
    eventTitle() { return (this.$root.config && this.$root.config.eventTitle) || ''; },
    eventDates() {
      // Compose a human label from the raw ISO strings plumbed through
      // $root.config (eventStartDate / eventEndDate), mirroring the
      // approach used by SessionPrintView.
      const cfg = this.$root.config || {};
      const start = formatDate(cfg.eventStartDate || '');
      const end = formatDate(cfg.eventEndDate || '');
      if (start && end && start !== end) return `${start} – ${end}`;
      return start || end || '';
    },
    scopeLabel() {
      return this.scope === 'all' ? 'Complete Agenda' : 'Search Results';
    },
    selectedDayValue() {
      return this.$store.getters.selectedDayValue || null;
    },
    activeFilterChips() {
      const ALLOWED_GROUPS = ['session_format', 'session_topics', 'session_credit_type'];
      const filters = this.$store.getters.filters || [];
      return filters
        .filter(g => ALLOWED_GROUPS.includes(g.id))
        .flatMap(g => (g.filters || []).filter(f => f.selected).map(f => f.value));
    },
    hasActiveFilters() {
      return !!(this.selectedDayValue || this.activeFilterChips.length);
    },
    dayLabel() {
      return this.selectedDayValue ? formatDayLabel(this.selectedDayValue) : '';
    },

    days() {
      // group by date string, preserving order
      const byDay = new Map();
      this.sessions.forEach(s => {
        const date = (s.meta && s.meta.startDate) || '';
        const label = formatDate(date) || 'TBD';
        if (!byDay.has(label)) byDay.set(label, []);
        byDay.get(label).push(s);
      });
      return Array.from(byDay.entries()).map(([label, list]) => ({
        label,
        groups: groupByStartTime(list, {
          sortOption: { fieldName: 'startTime' },
          searchTerm: '',
        }),
      }));
    },
  },

  methods: {
    formatTime,
    formatDate,
    creditFor(session) {
      const v = session.meta && session.meta.sessionCreditType;
      if (!v || v === 'Not for Credit') return '';
      return v;
    },
    isPreregRequired(session) {
      const value = session && session.meta && session.meta.preregRequired;
      if (value === undefined || value === null || value === false) return false;
      if (value === 'false' || value === 0 || value === '0') return false;
      return Boolean(value);
    },
  },
};
</script>

<style lang="scss" scoped>
// Per Figma 2203:21949.
.schedule-print {
  font-family: 'Public Sans', sans-serif;
  color: black;
  padding: 24px 32px;
}

.schedule-print__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 16px;
}

.schedule-print__event-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px;
}

.schedule-print__event-dates {
  font-size: 14px;
  color: #616161;
  margin: 0;
}

.schedule-print__scope-label {
  font-size: 20px;
  font-weight: 700;
}

.schedule-print__rule {
  border-bottom: 4px solid #163874;
  margin-bottom: 24px;
}

.schedule-print__day {
  margin-bottom: 24px;
}

.day-print-band {
  background: #163874;
  color: white;
  font-size: 14px;
  font-weight: 700;
  padding: 8px 16px;
  margin-bottom: 12px;
}

.schedule-print__concurrent-header {
  font-size: 13px;
  font-weight: 700;
  color: #163874;
  margin: 12px 0 4px;
}

.session-print-row {
  display: flex;
  gap: 24px;
  padding: 8px 0;
  border-bottom: 1px solid #e6e8eb;
  page-break-inside: avoid;
}

.session-print-row__time {
  flex: 0 0 180px;
  font-weight: 600;
  color: #163874;
  font-size: 13px;
}

.session-print-row__main {
  flex: 1;
  min-width: 0;
}

.session-print-row__title {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 4px;
}

.session-print-row__meta {
  font-size: 13px;
  color: #475569;
  margin-bottom: 4px;
}

.session-print-row__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.session-print-row__tag {
  height: 22px;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 12px;
  background: #f0f0f0;
  color: #1e1e1f;
  display: inline-flex;
  align-items: center;

  &--credit { background: #cfe4f8; color: #163874; }
}

.schedule-print__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.schedule-print__filters-label {
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  margin-right: 4px;
}

.schedule-print__filter-chip {
  height: 22px;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 12px;
  background: #f0f0f0;
  color: #1e1e1f;
  display: inline-flex;
  align-items: center;
}
</style>
